import MoreInfoButton from "@/components/more-info-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useServices } from "@/services/hooks/useServices";
import {
  DnsRecordsResponse,
  StructuredDnsRecords,
} from "@/types/postman.types";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CalendarPlus,
  CalendarX,
  CheckCircle2,
  ChevronDown,
  Clock,
  ExternalLink,
  Globe,
  Image,
  RefreshCw,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Get all records from structured format
const getStructuredRecords = (structuredRecords: StructuredDnsRecords) => {
  if (!structuredRecords) return [];

  return Object.entries(structuredRecords).map(([key, record]) => ({
    ...record,
    type: record.type || key,
    description: record.description || key,
  }));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getWhoisKey = (whoisData: any) => {
  if (!whoisData) return null;
  // Get the first key from the WHOIS data object
  return Object.keys(whoisData)[0] || null;
};

// Helper function to get WHOIS data regardless of registrar key
// eslint-disable-next-line @typescript-eslint/no-explicit-any, react-refresh/only-export-components
export const getWhoisData = (record: any) => {
  if (!record?.record?.record) return null;
  const whoisKey = getWhoisKey(record.record.record);
  return whoisKey ? record.record.record[whoisKey] : null;
};

const parseWhoisDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;

  // Extract the first date part (before any spaces)
  const datePart = dateStr.split(" ")[0];

  // Check if date is in the future
  const parsedDate = new Date(datePart);
  const now = new Date();

  // Validate the date - return null for invalid dates or dates in the future
  if (isNaN(parsedDate.getTime()) || parsedDate > now) {
    return null;
  }
  return parsedDate;
};

type Props = {
  recordsData: DnsRecordsResponse;
};

export const EmailHealthDomainAge = ({ recordsData }: Props) => {
  const records = useMemo(() => {
    return recordsData?.structuredRecords
      ? getStructuredRecords(
          recordsData.structuredRecords as StructuredDnsRecords
        )
      : [];
  }, [recordsData?.structuredRecords]);

  return (
    <div className="mb-6">
      <Card>
        <Collapsible defaultOpen>
          <CollapsibleTrigger className="w-full">
            <CardHeader className="p-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span>Domain Age</span>
                </CardTitle>{" "}
                <div className="flex items-center gap-3">
                  <MoreInfoButton slug="domain-age" />
                  <ChevronDown className="w-4 h-4 transition-transform duration-200" />
                </div>
              </div>
              <CardDescription className="self-start">
                {(() => {
                  const whoisRecord = records.find((r) => r.type === "WHOIS");
                  const whoisData = getWhoisData(whoisRecord);
                  const createdDate = whoisData?.["Created Date"];

                  return createdDate
                    ? `Domain created on ${new Date(
                        createdDate
                      ).toLocaleDateString("en-GB", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })}`
                    : "Domain creation date unavailable";
                })()}
              </CardDescription>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-4 border-t">
              {(() => {
                // Find the WHOIS record in our records array
                const whoisRecord = records.find((r) => r.type === "WHOIS");
                const whoisData = getWhoisData(whoisRecord);
                let domainCreatedDate;
                let domainUpdatedDate;
                let domainExpiryDate;
                if (
                  whoisData &&
                  whoisData["Expiry Date"] &&
                  whoisData["Created Date"]
                ) {
                  domainCreatedDate = whoisData["Created Date"].split("T")[0];
                  domainUpdatedDate = whoisData["Updated Date"].split("T")[0];
                  domainExpiryDate = whoisData["Expiry Date"].split("T")[0];
                }
                const redirectRecord = recordsData?.domainReport;

                if (!whoisData) {
                  return (
                    <div className="py-4 text-center text-muted-foreground">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>WHOIS data not available for this domain</p>
                    </div>
                  );
                }

                // Calculate domain age

                const now = new Date();
                const ageInYears = (
                  (now.getTime() -
                    (new Date(domainCreatedDate).getTime() as number) || 0) /
                  (1000 * 60 * 60 * 24 * 365.25)
                ).toFixed(1);

                // Calculate expiry time

                const daysLeft = Math.ceil(
                  (new Date(domainExpiryDate).getTime() - now.getTime()) /
                    (1000 * 60 * 60 * 24)
                );

                return (
                  <div className="space-y-6">
                    {/* Domain Age */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                        Domain Age
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Clock className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">Age</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-2xl font-bold text-slate-700 dark:text-slate-300 cursor-help">
                                  {(() => {
                                    // Calculate age in different units
                                    const ageInDays = Math.ceil(
                                      (now.getTime() -
                                        new Date(domainCreatedDate).getTime()) /
                                        (1000 * 60 * 60 * 24)
                                    );
                                    const ageInMonths = Math.floor(
                                      ageInDays / 30.44
                                    ); // Average days in a month
                                    const ageInYears = (
                                      ageInDays / 365.25
                                    ).toFixed(1);

                                    // Display in appropriate units based on age
                                    if (ageInDays < 30) {
                                      return (
                                        <>
                                          {ageInDays}{" "}
                                          <span className="text-sm font-normal">
                                            days
                                          </span>
                                        </>
                                      );
                                    } else if (ageInMonths < 12) {
                                      return (
                                        <>
                                          {ageInMonths}{" "}
                                          <span className="text-sm font-normal">
                                            months
                                          </span>
                                        </>
                                      );
                                    } else {
                                      return (
                                        <>
                                          {ageInYears}{" "}
                                          <span className="text-sm font-normal">
                                            years
                                          </span>
                                        </>
                                      );
                                    }
                                  })()}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent className="bg-blue-50 text-black border border-blue-200">
                                {(() => {
                                  if (!domainCreatedDate)
                                    return "Age data unavailable";

                                  const ageInDays = Math.ceil(
                                    (now.getTime() -
                                      new Date(domainCreatedDate).getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  );

                                  const ageInMonths = Math.floor(
                                    ageInDays / 30.44
                                  ); // Average days in a month
                                  const ageInYears = (
                                    ageInDays / 365.25
                                  ).toFixed(2);

                                  // Format the tooltip based on the same logic as display
                                  if (ageInDays < 30) {
                                    return `Exactly ${ageInDays} days`;
                                  } else if (ageInMonths < 12) {
                                    return `${ageInMonths} months (${ageInDays} days)`;
                                  } else {
                                    return `${ageInYears} years (${ageInDays} days)`;
                                  }
                                })()}
                              </TooltipContent>
                            </Tooltip>

                            {/* Age Classification Tag */}
                            {(() => {
                              if (!domainCreatedDate) {
                                return (
                                  <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                                    Unknown
                                  </Badge>
                                );
                              }

                              const ageInDays = Math.ceil(
                                (now.getTime() -
                                  new Date(domainCreatedDate).getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );

                              if (ageInDays < 30) {
                                return (
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 shadow-none dark:bg-red-900/20 dark:text-red-500 dark:hover:bg-red-900/20">
                                    New Domain
                                  </Badge>
                                );
                              } else if (ageInDays >= 30 && ageInDays <= 90) {
                                return (
                                  <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 shadow-none dark:bg-yellow-900/20 dark:text-yellow-500 dark:hover:bg-yellow-900/20">
                                    Recent Domain
                                  </Badge>
                                );
                              } else {
                                return (
                                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 shadow-none dark:bg-green-900/20 dark:text-green-500 dark:hover:bg-green-900/20">
                                    Established Domain
                                  </Badge>
                                );
                              }
                            })()}
                          </div>

                          {/* Domain Age Meter */}
                          <div className="mt-4">
                            <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                              {(() => {
                                const ageInDays = domainCreatedDate
                                  ? Math.ceil(
                                      (now.getTime() -
                                        new Date(domainCreatedDate).getTime()) /
                                        (1000 * 60 * 60 * 24)
                                    )
                                  : 0;

                                // Cap at 365 days (1 year) for the progress bar
                                const percentage = Math.min(
                                  (ageInDays / 90) * 100,
                                  100
                                );

                                let bgColor = "bg-red-500";
                                if (ageInDays >= 90) bgColor = "bg-green-500";
                                else if (ageInDays >= 30)
                                  bgColor = "bg-yellow-500";

                                return (
                                  <div
                                    className={`h-full ${bgColor} transition-all duration-500`}
                                    style={{ width: `${percentage}%` }}
                                  />
                                );
                              })()}
                            </div>
                            <div className="flex justify-between mt-1 text-xs text-slate-500 dark:text-slate-400">
                              <span>0 days</span>
                              <span>30 days</span>
                              <span>90 days</span>
                              {/* <span>1 year</span> */}
                            </div>
                          </div>

                          {/* Classification Info */}
                          <div className="mt-4 text-xs text-slate-600 dark:text-slate-400">
                            {(() => {
                              const createdDate = parseWhoisDate(
                                whoisData["Created Date"]
                              );

                              if (!createdDate) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Domain age unavailable</strong> -
                                      The creation date in the WHOIS record
                                      appears to be invalid.
                                    </span>
                                  </div>
                                );
                              }

                              const ageInDays = Math.ceil(
                                (now.getTime() - createdDate.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );

                              if (ageInDays < 30) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>New domains</strong> (less than 30
                                      days) may have lower deliverability as
                                      they haven't established reputation with
                                      email providers.
                                    </span>
                                  </div>
                                );
                              } else if (ageInDays >= 30 && ageInDays <= 90) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Recent domains</strong> (30-90
                                      days) have started building reputation but
                                      may still face some deliverability
                                      challenges.
                                    </span>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Established domains</strong> (over
                                      90 days) typically have better
                                      deliverability and are less likely to be
                                      flagged as suspicious.
                                    </span>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-secondary/50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4 text-blue-500" />
                            <span className="font-medium">Expires In</span>
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className="text-2xl font-bold text-slate-700 dark:text-slate-300 cursor-help">
                                  {(() => {
                                    // Calculate expiry time
                                    const now = new Date();

                                    if (!domainExpiryDate) {
                                      return <>N/A</>;
                                    }

                                    const daysLeft = Math.ceil(
                                      (new Date(domainExpiryDate).getTime() -
                                        now.getTime()) /
                                        (1000 * 60 * 60 * 24)
                                    );

                                    return (
                                      <>
                                        {daysLeft}{" "}
                                        <span className="text-sm font-normal">
                                          days
                                        </span>
                                      </>
                                    );
                                  })()}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent className="bg-blue-50 text-black border border-blue-200">
                                {(() => {
                                  if (!domainExpiryDate)
                                    return "Expiry data unavailable";

                                  const daysLeft = Math.ceil(
                                    (new Date(domainExpiryDate).getTime() -
                                      now.getTime()) /
                                      (1000 * 60 * 60 * 24)
                                  );

                                  const monthsLeft = Math.floor(
                                    daysLeft / 30.44
                                  );
                                  const yearsLeft = (daysLeft / 365.25).toFixed(
                                    2
                                  );

                                  const expiryDate = new Date(
                                    domainExpiryDate
                                  ).toLocaleDateString("en-GB", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  });

                                  // Format the tooltip using similar logic to the domain age display
                                  let timeFormatted;
                                  if (daysLeft < 30) {
                                    timeFormatted = `${daysLeft} days`;
                                  } else if (monthsLeft < 12) {
                                    timeFormatted = `${monthsLeft} months (${daysLeft} days)`;
                                  } else {
                                    timeFormatted = `${yearsLeft} years (${daysLeft} days)`;
                                  }

                                  return `Expires in ${timeFormatted} on ${expiryDate}`;
                                })()}
                              </TooltipContent>
                            </Tooltip>

                            {/* Expiry Classification Tag */}
                            {(() => {
                              if (!domainExpiryDate) {
                                return (
                                  <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
                                    Unknown
                                  </Badge>
                                );
                              }

                              const daysLeft = Math.ceil(
                                (new Date(domainExpiryDate).getTime() -
                                  now.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );

                              if (daysLeft < 30) {
                                return (
                                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 shadow-none dark:bg-red-900/20 dark:text-red-500 dark:hover:bg-red-900/20">
                                    Critical
                                  </Badge>
                                );
                              } else if (daysLeft >= 30 && daysLeft <= 90) {
                                return (
                                  <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 shadow-none dark:bg-yellow-900/20 dark:text-yellow-500 dark:hover:bg-yellow-900/20">
                                    Warning
                                  </Badge>
                                );
                              } else {
                                return (
                                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100 shadow-none dark:bg-green-900/20 dark:text-green-500 dark:hover:bg-green-900/20">
                                    Good
                                  </Badge>
                                );
                              }
                            })()}
                          </div>

                          {/* Domain Expiry Meter */}
                          <div className="mt-4">
                            <div className="h-2 w-full bg-red-500 rounded-full overflow-hidden">
                              {(() => {
                                const expiryDate = domainExpiryDate;
                                const now = new Date();

                                if (!expiryDate) {
                                  return null;
                                }

                                // Calculate days left
                                const daysLeft = Math.ceil(
                                  (new Date(domainExpiryDate).getTime() -
                                    now.getTime()) /
                                    (1000 * 60 * 60 * 24)
                                );

                                // Calculate percentage based on 95 days max
                                const percentage = Math.min(
                                  (daysLeft / 95) * 100,
                                  100
                                );

                                return (
                                  <div
                                    className="h-full bg-green-500 transition-all duration-500"
                                    style={{ width: `${percentage}%` }}
                                  />
                                );
                              })()}
                            </div>
                            <div className="flex justify-between mt-1 text-xs text-slate-500 dark:text-slate-400">
                              <span>0 days</span>
                              <span>30 days</span>
                              <span>90 days</span>
                            </div>
                          </div>

                          {/* Classification Info */}
                          <div className="mt-4 text-xs text-slate-600 dark:text-slate-400">
                            {(() => {
                              if (!domainExpiryDate) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>
                                        Expiration date unavailable
                                      </strong>{" "}
                                      - The expiration date in the WHOIS record
                                      appears to be invalid.
                                    </span>
                                  </div>
                                );
                              }

                              const daysLeft = Math.ceil(
                                (new Date(domainExpiryDate).getTime() -
                                  now.getTime()) /
                                  (1000 * 60 * 60 * 24)
                              );

                              if (daysLeft < 30) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Critical:</strong> Your domain
                                      expires soon. Renew immediately to avoid
                                      service interruption and loss of domain
                                      control.
                                    </span>
                                  </div>
                                );
                              } else if (daysLeft >= 30 && daysLeft <= 90) {
                                return (
                                  <div className="flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Warning:</strong> Your domain will
                                      expire within 90 days. Consider renewing
                                      soon to ensure continued operation.
                                    </span>
                                  </div>
                                );
                              } else {
                                return (
                                  <div className="flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                    <span>
                                      <strong>Good:</strong> Your domain is not
                                      due for renewal soon. You have plenty of
                                      time before expiration.
                                    </span>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Domain Timeline */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                        Domain Timeline
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-start space-x-3">
                          <div className="bg-green-100 dark:bg-green-900/20 p-1 rounded-full mt-0.5">
                            <CalendarPlus className="w-3.5 h-3.5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Created</p>
                            {/* {parseWhoisDate(domainCreatedDate ) ? ( */}
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {domainCreatedDate
                                ? new Date(
                                    domainCreatedDate
                                  ).toLocaleDateString("en-GB", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })
                                : "Unknown"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="bg-blue-100 dark:bg-blue-900/20 p-1 rounded-full mt-0.5">
                            <RefreshCw className="w-3.5 h-3.5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Last Updated</p>

                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {domainUpdatedDate
                                ? new Date(
                                    domainUpdatedDate
                                  ).toLocaleDateString("en-GB", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })
                                : new Date(
                                    whoisData["Updated Date"]
                                  ).toLocaleDateString("en-GB", {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="bg-red-100 dark:bg-red-900/20 p-1 rounded-full mt-0.5">
                            <CalendarX className="w-3.5 h-3.5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">Expires</p>
                            {parseWhoisDate(domainExpiryDate) ? (
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {new Date(domainExpiryDate).toLocaleDateString(
                                  "en-GB",
                                  {
                                    year: "numeric",
                                    month: "2-digit",
                                    day: "2-digit",
                                  }
                                )}
                              </p>
                            ) : (
                              <p className="text-sm text-slate-600 dark:text-slate-400">
                                {domainExpiryDate
                                  ? new Date(
                                      domainExpiryDate
                                    ).toLocaleDateString("en-GB", {
                                      year: "numeric",
                                      month: "2-digit",
                                      day: "2-digit",
                                    })
                                  : "Invalid Date"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Domain Meta Information */}
                    <div>
                      <h4 className="text-sm font-semibold mb-3 text-slate-700 dark:text-slate-300 border-b pb-1">
                        Registry Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-4 text-sm">
                        <div className="flex items-start gap-2">
                          <span className="font-medium text-slate-600 dark:text-slate-400">
                            Domain Name:
                          </span>
                          <span className="text-slate-800 dark:text-slate-200">
                            {whoisData["Domain Name"]}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-medium text-slate-600 dark:text-slate-400">
                            Registry ID:
                          </span>
                          <span className="text-slate-800 dark:text-slate-200">
                            {whoisData["Registry Domain ID"]}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-medium text-slate-600 dark:text-slate-400">
                            Registrar:
                          </span>
                          <span className="text-slate-800 dark:text-slate-200">
                            {whoisData["Registrar"]}
                          </span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-medium text-slate-600 dark:text-slate-400">
                            IANA ID:
                          </span>
                          <span className="text-slate-800 dark:text-slate-200">
                            {whoisData["Registrar IANA ID"]}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
};

export const EmailHealthDomainReputation = ({
  domainReport,
}: {
  domainReport: DnsRecordsResponse["domainReport"];
}) => {
  const [domainImage, setDomainImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { postmasterService } = useServices();

  // Use useRef to track if we've already fetched the image
  const hasFetched = useRef(false);

  // Extract the redirect URL logic
  const redirectUrl = useMemo(() => {
    return (
      domainReport?.redirectResult?.redirectUrl ||
      domainReport?.redirectResult?.originalUrl
    );
  }, [
    domainReport?.redirectResult?.redirectUrl,
    domainReport?.redirectResult?.originalUrl,
  ]);

  // Fetch domain image function
  const fetchDomainImage = useCallback(async () => {
    if (!redirectUrl) return;

    try {
      setIsLoading(true);
      const image = await postmasterService.getDomainImage(redirectUrl);
      setDomainImage(image);
    } catch (error) {
      console.error("Failed to fetch domain image:", error);
      setDomainImage(null);
    } finally {
      setIsLoading(false);
    }
  }, [redirectUrl, postmasterService]);

  // Main effect to handle image fetching
  useEffect(() => {
    if (!hasFetched.current && redirectUrl) {
      fetchDomainImage();
      hasFetched.current = true;
    }
  }, [redirectUrl, fetchDomainImage]);

  return (
    <div className="mb-6">
      <Card className="w-full">
        <Collapsible defaultOpen>
          <CollapsibleTrigger asChild className="group">
            <CardHeader className="flex p-4 flex-row items-center justify-between space-y-0 cursor-pointer">
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4 text-blue-500" />
                <CardTitle className="text-sm font-medium">
                  Domain Redirection
                </CardTitle>
              </div>
              <div className="flex items-center space-x-3">
                {domainReport?.redirectResult?.redirect ? (
                  <span className="text-sm font-medium text-green-600">
                    Yes
                  </span>
                ) : (
                  <span className="text-sm font-medium text-red-600">No</span>
                )}

                <ChevronDown className="h-4 w-4" />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="p-4 space-y-6 border-t">
              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Domain Redirection Screenshot
                </h3>
                <div className="bg-slate-50 dark:bg-secondary/50 rounded-lg p-4">
                  {domainImage ? (
                    <img
                      src={domainImage}
                      alt="Domain Redirection Screenshot"
                      className="w-full max-w-2xl mx-auto rounded-md"
                    />
                  ) : (
                    <div className="w-full flex items-center gap-2">
                      <Image size={18} className="text-muted-foreground" />
                      <span className="text-sm text-slate-500">
                        No screenshot available
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              <div>
                <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                  Domain redirection Url
                </h3>
                {domainReport?.redirectResult?.redirect &&
                domainReport?.redirectResult?.redirectUrl ? (
                  <div className="grid grid-cols-[1fr_40px]">
                    <div className="flex-1 p-2.5 bg-slate-50 dark:bg-secondary/50 rounded-l-md text-sm text-blue-600 break-all">
                      {domainReport?.redirectResult?.redirectUrl}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-l-none border-l-0 h-full self-stretch"
                      onClick={() =>
                        window.open(
                          domainReport?.redirectResult?.redirectUrl || "",
                          "_blank"
                        )
                      }
                    >
                      <ExternalLink className="h-4 w-4" />
                      <span className="sr-only">Visit URL</span>
                    </Button>
                  </div>
                ) : (
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-500">
                    No redirection URL configured
                  </div>
                )}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  );
};
